import sections from "./foilChartData.json";

export const FOIL_SECTIONS = sections;
export const FOIL_COUNT = sections.reduce((n, s) => n + s.foils.length, 0);
export const ALL_FOILS = sections.flatMap((s) => s.foils.map((f) => ({ ...f, family: s.id, familyTitle: s.title })));

export const FAMILIES = {
  metallized: {
    short: "Metallic",
    finish: "Bright metallic",
    blurb: "Classic golds, silvers, coppers, rose golds and tinted metallics. The mirror-bright finish that reads as “foil” from across the room. Our standards: 120 gold, 110 silver and 142 copper.",
    bestOn: "Smooth and silk cover stocks, Colorplan, black and dark papers.",
    watch: "Large solid areas can pinhole on heavily textured felt or linen stock.",
  },
  holographic: {
    short: "Holographic",
    finish: "Rainbow diffraction",
    blurb: "Full-spectrum rainbow and oil-slick patterns that shift with the light. Built for product packaging, event badges and anything that needs to pop under lights.",
    bestOn: "Coated and silk stocks, black stock for maximum contrast.",
    watch: "Fine type under 8 pt can disappear into the pattern. Keep logos bold.",
  },
  "holographic-specialty": {
    short: "Specialty",
    finish: "Patterned, pearl & clear",
    blurb: "Patterned holographics, translucent white pearl, clear gloss and other effect foils for luxury packaging and stationery.",
    bestOn: "Dark or coloured stock for pearl and clear foils; any smooth stock for patterns.",
    watch: "Translucent foils pick up the paper colour underneath; always sample first.",
  },
  "matte-pigments": {
    short: "Matte pigment",
    finish: "Flat, non-metallic",
    blurb: "Opaque matte colours with zero shine. Think a crisp white on kraft, a true black on Colorplan or a brand colour that offset ink cannot hit on dark paper.",
    bestOn: "Kraft, chipboard, Colorplan and uncoated stocks — anywhere ink would sink in.",
    watch: "Matte pigments show fingerprints less than gloss, but scuff on high-traffic packaging.",
  },
  "gloss-pigments": {
    short: "Gloss pigment",
    finish: "Opaque, high gloss",
    blurb: "The same opaque coverage as matte pigments with a wet-look gloss. Ideal for brand colours on dark stock and bold packaging accents.",
    bestOn: "Smooth cover stock, dark and black papers, rigid boxes.",
    watch: "Gloss highlights halftone edges — supply vector artwork, never a raster logo.",
  },
};

export const familyOf = (id) => FAMILIES[id] || { short: id, finish: "", blurb: "", bestOn: "", watch: "" };

// Kolt house standards — the foils we stock and run by default.
export const STANDARD_FOILS = [["120", "Standard Gold"], ["110", "Standard Silver"], ["142", "Standard Copper"]];
export const standardLabel = (code) => (STANDARD_FOILS.find(([c]) => c === String(code || "").toUpperCase()) || [])[1] || null;

export const foilTags = (foil) => {
  const c = foil.code.toUpperCase();
  const tags = [];
  if (standardLabel(c)) tags.unshift(standardLabel(c));
  if (c.startsWith("SCRATCH")) tags.push("Scratch-off");
  if (c.startsWith("PW")) tags.push("Pearl / translucent");
  if (c.startsWith("CG")) tags.push("Clear gloss");
  if (/^H[KUT]/.test(c)) tags.push("Holographic pattern");
  if (/^M[RKM]/.test(c)) tags.push("Metallic variant");
  return tags;
};

export const findFoil = (code) => ALL_FOILS.find((f) => f.code.toLowerCase() === String(code || "").toLowerCase()) || null;

export const FOIL_PRODUCTS = [
  { slug: "foil-stamp-wedding-invitations", name: "Foil Stamp Wedding Invitations" },
  { slug: "foil-stamped-embossed-luxury-pocket-folder", name: "Foil + Embossed Pocket Folders" },
  { slug: "foil-stamped-letterheads", name: "Foil Stamped Letterheads" },
  { slug: "foil-stamp-postcard", name: "Hot Foil Postcards" },
  { slug: "foil-wedding-envelopes", name: "Foil Wedding Envelopes" },
  { slug: "premium-linen-folder-with-foil-stamping", name: "Linen Folders with Foil" },
];

export const FOIL_FAQ = [
  { q: "How do I specify a foil color on my order?", a: `Write the foil code exactly as it appears on this chart (for example “Foil 120” or “Foil HU941”) in your quote request or order notes, one code per foil color. Each color needs its own stamping die and press pass, so two foil colors cost more than one.` },
  { q: "What is the difference between metallic, pigment and holographic foil?", a: "Metallic foils are mirror-bright golds, silvers and tinted metallics. Pigment foils are opaque flat colours — matte or gloss — that give you a solid white, black or brand colour on dark or kraft stock where ink would sink in. Holographic foils diffract light into rainbow or patterned effects." },
  { q: "Will the foil look exactly like the swatch on my screen?", a: "No. These swatches are photographs of real foil on white stock, and every screen renders colour differently. Metallic and holographic foils also shift with the viewing angle and the colour of the paper underneath. For a critical brand match, request a physical sample." },
  { q: "Can I foil stamp on dark or coloured paper?", a: "Yes — foil is opaque, which is exactly why it is the go-to for white, gold or brand colours on black, navy and kraft stock. Pigment and metallic foils cover dark paper fully; translucent pearl and clear foils will pick up the paper colour." },
  { q: "What artwork do I need for foil stamping?", a: "Vector artwork (PDF, AI or EPS) with the foil areas on their own layer or spot colour, minimum 0.5 pt line weight and 6 pt type. Knock-out (reversed) type should be at least 1 pt. Our prepress team checks every file before dies are made." },
  { q: "How long does foil stamping add to turnaround?", a: "Allow one to two extra business days for die making and make-ready on top of standard production. Rush options are available — digital foil is the fastest alternative when there is no time for dies." },
];
